// Card d'un UnifiedLead — QUI / POURQUOI MAINTENANT / ACTION RECOMMANDÉE
// Aucune fabrication : si le backend renvoie "unknown" / "not_found",
// on l'affiche tel quel avec un ton neutre.
import { useState } from "react";
import { Building2, MapPin, TrendingUp, Eye, Layers, Clock,
         Search } from "lucide-react";
import { NAVY, CHAMPAGNE, SLATE, GOLD } from
  "@/components/altiora/PremiumUI";
import {
  contactabilityBadge, tierBadge, crossSourceLabel,
  humanKind, humanSource,
} from "@/lib/altiora-leads-i18n";
import LifecycleActions from
  "@/components/altiora-leads/LifecycleActions";
import ChannelBadges from
  "@/components/altiora-leads/ChannelBadges";
import Canada411LookupDialog from
  "@/components/altiora-leads/Canada411LookupDialog";

function isKnown(v) {
  return v && v !== "unknown" && v !== "not_found" && v !== "";
}

export default function UnifiedLeadCard({
  lead, record, transitionsSchema,
  onOpenEvidence, onOpenHistory,
  onLifecycleChange, onCrmSync, onUpsert,
}) {
  const badge = contactabilityBadge(lead?.contactability?.status);
  const tier = tierBadge(lead?.tier);
  const addr = lead?.address || {};
  const city = addr.city;
  const province = addr.province;
  const address = [city, province].filter(isKnown).join(", ");
  const isCross = !!lead?.cross_source;
  const sources = lead?.sources || [];

  // Contactability V2 · per-channel statuses come from the persisted
  // record (enrichment pipeline). If they don't exist yet, ChannelBadges
  // renders nothing and we fall back to the monolithic badge.
  const hasChannelStatuses = !!(record?.phone_status
                                 || record?.email_status
                                 || record?.sms_status);
  const [canadaOpen, setCanadaOpen] = useState(false);
  const canOpenCanada411 = !!lead?.entity_id && !!lead?.decision_maker?.name
    && lead.decision_maker.name !== "not_found";

  return (
    <article
      data-testid={`unified-lead-card-${lead.entity_id}`}
      className="rounded-xl bg-white p-5 flex flex-col gap-4"
      style={{ border: `1px solid ${CHAMPAGNE}44`,
               boxShadow: "0 6px 20px -14px rgba(11,37,69,0.18)" }}>

      {/* HEADER — QUI */}
      <header className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span data-testid="lead-tier-badge"
                  className="text-[10px] font-mono uppercase tracking-[0.22em] px-2 py-0.5 rounded-full"
                  style={{ background: tier.bg, color: tier.color }}>
              {tier.label}
            </span>
            <span className="text-[11px] font-mono" style={{ color: SLATE }}>
              {humanKind(lead.lead_type)}
            </span>
            {isCross && (
              <span data-testid="lead-cross-source"
                    className="text-[10px] font-mono uppercase tracking-[0.22em]
                               px-2 py-0.5 rounded-full flex items-center gap-1"
                    style={{ background: `${GOLD}33`, color: NAVY }}>
                <Layers className="h-3 w-3" /> {crossSourceLabel(sources)}
              </span>
            )}
          </div>
          <h3 className="font-serif text-[18px] mt-2 leading-tight"
              style={{ color: NAVY }}
              data-testid="lead-entity-name">
            {lead.entity_name}
          </h3>
          <div className="text-[12px] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1"
               style={{ color: SLATE }}>
            {isKnown(lead.bn) && (
              <span className="font-mono">NE {lead.bn}</span>
            )}
            {address && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {address}
              </span>
            )}
            {isKnown(lead.jurisdiction) && (
              <span>Juridiction : {lead.jurisdiction}</span>
            )}
          </div>
        </div>

        {/* Contactability — granular V2 badges if enriched,
            legacy monolithic block otherwise. */}
        {hasChannelStatuses ? (
          <div className="min-w-[130px] flex flex-col items-end gap-1">
            <span data-testid="lead-contactability-v2-label"
                  className="text-[9px] font-mono uppercase tracking-[0.18em]"
                  style={{ color: SLATE }}>
              Contactabilité
            </span>
            <ChannelBadges record={record} />
          </div>
        ) : (
          <div data-testid="lead-contactability-badge"
               className="rounded-lg px-3 py-2 text-center min-w-[130px]"
               style={{ background: badge.bg }}
               title={badge.hint}>
            <div className="text-[16px]">{badge.emoji}</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] mt-1"
                 style={{ color: badge.color }}>
              {badge.label}
            </div>
          </div>
        )}
      </header>

      {/* POURQUOI MAINTENANT */}
      <section data-testid="lead-why-now"
               className="rounded-lg p-3"
               style={{ background: "#FBF6EA", border: `1px solid ${CHAMPAGNE}33` }}>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] mb-1"
             style={{ color: NAVY }}>
          <TrendingUp className="h-3 w-3" style={{ color: GOLD }} />
          Pourquoi maintenant
        </div>
        <p className="text-[13px]" style={{ color: NAVY }}>
          {lead.why_now && lead.why_now !== "not_found"
            ? lead.why_now
            : "Aucun signal daté récent — cette entité est surveillée sans déclencheur immédiat."}
        </p>
        {lead.why_for_me && lead.why_for_me !== "unknown" && (
          <p className="mt-2 text-[12px]" style={{ color: SLATE }}>
            {lead.why_for_me}
          </p>
        )}
      </section>

      {/* ACTION RECOMMANDÉE */}
      <section data-testid="lead-recommended-action">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1"
             style={{ color: SLATE }}>
          Action recommandée
        </div>
        <p className="text-[13px]" style={{ color: NAVY }}>
          {lead.recommended_action && lead.recommended_action !== "unknown"
            ? lead.recommended_action
            : "Aucune action recommandée par le moteur — signal insuffisant."}
        </p>
      </section>

      {/* FOOTER — score + preuves */}
      <footer className="flex items-center justify-between gap-3 pt-3"
              style={{ borderTop: `1px solid ${CHAMPAGNE}22` }}>
        <div className="flex items-center gap-4 text-[11px]" style={{ color: SLATE }}>
          <span className="flex items-center gap-1">
            <Building2 className="h-3 w-3" />
            {sources.length > 0
              ? sources.map(humanSource).join(" · ")
              : "Source non renseignée"}
          </span>
          <span data-testid="lead-score">
            Score {lead.score?.toFixed?.(1) ?? lead.score} · Confiance {Math.round((lead.confidence || 0) * 100)}%
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canOpenCanada411 && (
            <button
              data-testid="lead-canada411-lookup"
              onClick={() => setCanadaOpen(true)}
              className="text-[11px] flex items-center gap-1 px-3 py-1.5 rounded-full"
              style={{ background: `${GOLD}22`, color: NAVY,
                       border: `1px solid ${GOLD}88` }}
              title="Vérifier manuellement le décideur sur Canada411">
              <Search className="h-3 w-3" /> Canada411
            </button>
          )}
          <button
            data-testid="lead-open-evidence"
            onClick={() => onOpenEvidence?.(lead)}
            className="text-[12px] flex items-center gap-1 px-3 py-1.5 rounded-full"
            style={{ background: NAVY, color: "#FBF6EA" }}>
            <Eye className="h-3 w-3" /> Voir les preuves
          </button>
        </div>
      </footer>

      {/* Actions cycle de vie — persistées côté backend */}
      <LifecycleActions
        lead={lead}
        record={record}
        transitionsSchema={transitionsSchema}
        onChange={onLifecycleChange}
        onCrmSync={onCrmSync}
        onUpsert={onUpsert} />

      {record && (
        <button
          data-testid="lead-open-history"
          onClick={() => onOpenHistory?.(lead)}
          className="text-[11px] flex items-center gap-1 self-start"
          style={{ color: SLATE }}>
          <Clock className="h-3 w-3" /> Historique du lead
        </button>
      )}

      <Canada411LookupDialog
        open={canadaOpen}
        onClose={() => setCanadaOpen(false)}
        lead={lead}
        record={record}
        onDone={() => onUpsert?.(lead)} />
    </article>
  );
}
